// İSG uzmanı portalı — Taşeron detayı: Araçlar sekmesi (api-contract §23). Araç kaydı taşeron
// düzeyindedir; izinler ona bağlanır, kopyalamaz. Liste `contractorsKeys.vehicles` ile tutulur →
// `invalidateVehicleCaches` tazeler. Sheet ve yükleme hedefi SATIR DEĞİL uuid saklar: her render'da
// güncel sorgu verisinden bulunur, aksi hâlde belge listesi mutasyondan sonra tazelenmez.
// Form çekmecesi yalnız bu sekmenin kendi denetimlerinden açılır, `VehicleSheet` içinden asla.

import {
  Button,
  ConfirmDialog,
  type ContractorVehicleDto,
  EmptyState,
  RowActionsMenu,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  VehicleDocStatus,
  VehicleFormSheet,
  type VehicleFormTarget,
  VehicleSheet,
  canEditPlate,
  resolveApiErrorMessage,
  useRowRemovalFocus,
  vehicleDeleteBlock,
  vehicleDeleteLabelKey,
  vehiclesApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Truck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { invalidateVehicleCaches } from '../../lib/vehicleCache';
import { useVehicleClient } from '../../lib/vehicleClient';
import { TabPanelIntro } from '../TabPanelIntro';
import { escapeStaysInReviewRow } from '../documents/DocumentReviewActions';
import { VehicleDocumentLinkModal } from '../vehicles/VehicleDocumentLinkModal';
import { VehicleDocuments } from '../vehicles/VehicleDocuments';

const COLUMN_COUNT = 6;

export function VehiclesPanel({ contractorUuid }: { contractorUuid: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const vehicleClient = useVehicleClient(contractorUuid);
  const query = useQuery({
    queryKey: contractorsKeys.vehicles(contractorUuid),
    queryFn: () => vehiclesApi.listContractorVehicles(contractorUuid),
  });
  const rows = query.data ?? [];

  const [formTarget, setFormTarget] = useState<VehicleFormTarget | null>(null);
  const [sheetUuid, setSheetUuid] = useState<string | null>(null);
  const [uploadUuid, setUploadUuid] = useState<string | null>(null);
  // Yükleme penceresi oturumu: hedef her null→uuid (ya da uuid→başka uuid) geçişinde artar;
  // modal `key` olarak alır → yeni açılış taze durumla başlar, kapanış AYNI örnekte kalır
  // (kapanış animasyonu, odak dönüşü, uçuştaki yüklemenin onSuccess/onError'ı korunur).
  const [uploadPrev, setUploadPrev] = useState<string | null>(null);
  const [uploadSession, setUploadSession] = useState(0);
  if (uploadUuid !== uploadPrev) {
    setUploadPrev(uploadUuid);
    if (uploadUuid !== null) {
      setUploadSession((n) => n + 1);
    }
  }
  const [toDelete, setToDelete] = useState<ContractorVehicleDto | null>(null);
  const addOpenerRef = useRef<HTMLButtonElement | null>(null);
  const plateOpenerRef = useRef<HTMLElement | null>(null);
  const formRestoreRef = useRef<HTMLElement | null>(null);
  const plateButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const rowKeys = useMemo(() => rows.map((v) => v.uuid), [rows]);

  const sheetVehicle = rows.find((v) => v.uuid === sheetUuid) ?? null;
  const uploadVehicle = rows.find((v) => v.uuid === uploadUuid) ?? null;

  // Hedef araç listeden kalkınca saklanan uuid bayatlamasın: odak efekti örtü açık sanmasın ve
  // sonradan yeniden bağlanan/eklenen aynı araç eski durumla kendiliğinden açılmasın.
  useEffect(() => {
    if (sheetUuid !== null && sheetVehicle === null) {
      setSheetUuid(null);
    }
    if (uploadUuid !== null && uploadVehicle === null) {
      setUploadUuid(null);
    }
  }, [sheetUuid, sheetVehicle, uploadUuid, uploadVehicle]);

  // Satır menüsünden açılan modallar (yükle / sil) odağı o satırın plaka düğmesine döndürür; silinen
  // satır listeden kalkınca ve diyalog kapanınca odak "Araç ekle"ye iner (ortak durum makinesi).
  const focus = useRowRemovalFocus({
    rowKeys,
    stamps: { dataUpdatedAt: query.dataUpdatedAt, errorUpdatedAt: query.errorUpdatedAt },
    addButtonRef: addOpenerRef,
    dialogOpen: toDelete !== null,
    otherSurfaceOpen: formTarget !== null || sheetVehicle !== null || uploadVehicle !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: (vehicleUuid: string) =>
      vehiclesApi.deleteContractorVehicle(contractorUuid, vehicleUuid),
    onSuccess: (_data, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, contractorUuid);
      // Araç sunucuda silindi → diyaloğun kapanış dönüşü artık "Araç ekle"ye iner.
      focus.removed(vehicleUuid);
      setToDelete(null);
    },
    // 409 HAS_OPEN_PERMITS: bayat olgu → tazele; satır yenilemede kalkmış olabilir.
    onError: (_error, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, contractorUuid);
      focus.removeFailed(vehicleUuid);
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <TabPanelIntro
        action={
          <Button
            ref={addOpenerRef}
            size="sm"
            onClick={() => {
              formRestoreRef.current = addOpenerRef.current;
              setFormTarget({ mode: 'create' });
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('vehicles.add')}
          </Button>
        }
      >
        {t('vehicles.subtitle')}
      </TabPanelIntro>

      {/* `relative`: sütun başlığındaki `sr-only` metnin kapsayıcı bloğu tablo olsun — aksi hâlde
          taşan tabloda sağ kenardaki mutlak konumlu metin kaydırıcıdan kaçar ve sayfayı yatay kaydırır.
          Açıklama sütunu sıkışmayı emer (≥sm `w-full` + `max-w-0`: içerik genişliği tabloya katkı
          vermez, sütun kalan genişliği alır ve kırpılır); <sm tablo zaten kendi kabında kayar →
          sabit 12rem. */}
      <Table caption={t('vehicles.title')} className="relative">
        <TableHeader>
          <TableRow>
            <TableHead>{t('vehicles.col.plate')}</TableHead>
            <TableHead className="w-full truncate sm:max-w-0">
              {t('vehicles.col.description')}
            </TableHead>
            <TableHead className="text-right">{t('vehicles.col.documents')}</TableHead>
            <TableHead>{t('vehicles.col.docStatus')}</TableHead>
            <TableHead>{t('vehicles.col.openPermits')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('contractors.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isPending ? (
            <TableMessageRow colSpan={COLUMN_COUNT}>
              <Spinner label={t('contractors.common.loading')} />
            </TableMessageRow>
          ) : query.isError ? (
            <TableMessageRow colSpan={COLUMN_COUNT}>
              {resolveApiErrorMessage(query.error, t)}
            </TableMessageRow>
          ) : rows.length === 0 ? (
            <TableMessageRow colSpan={COLUMN_COUNT}>
              <EmptyState icon={Truck} title={t('vehicles.empty')} />
            </TableMessageRow>
          ) : (
            rows.map((vehicle) => (
              <TableRow key={vehicle.uuid}>
                <TableCell truncate={false}>
                  <div className="max-w-[8rem]">
                    <button
                      type="button"
                      ref={(el) => {
                        if (el) {
                          plateButtonRefs.current.set(vehicle.uuid, el);
                        } else {
                          plateButtonRefs.current.delete(vehicle.uuid);
                        }
                      }}
                      title={vehicle.plate}
                      onClick={(e) => {
                        plateOpenerRef.current = e.currentTarget;
                        setSheetUuid(vehicle.uuid);
                      }}
                      className="block w-full truncate text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {vehicle.plate}
                    </button>
                  </div>
                </TableCell>
                <TableCell truncate={false} className="text-muted sm:max-w-0">
                  <span
                    className="block w-48 truncate sm:w-auto"
                    title={vehicle.description ?? undefined}
                  >
                    {vehicle.description ?? '—'}
                  </span>
                </TableCell>
                <TableCell className="tnum text-right">{vehicle.documents.length}</TableCell>
                <TableCell>
                  <VehicleDocStatus documents={vehicle.documents} actor="host" />
                </TableCell>
                <TableCell>
                  {vehicle.hasOpenPermits ? (
                    <span className="text-sm text-muted">{t('vehicles.openPermits.yes')}</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </TableCell>
                <TableCell className="w-12 px-2">
                  <RowActionsMenu
                    ariaLabel={t('vehicles.action.openMenu', { plate: vehicle.plate })}
                    items={[
                      {
                        key: 'upload',
                        label: t('vehicles.action.upload'),
                        onSelect: () => {
                          focus.rememberOpener(plateButtonRefs.current.get(vehicle.uuid) ?? null);
                          setUploadUuid(vehicle.uuid);
                        },
                      },
                      {
                        key: 'edit',
                        label: t('vehicles.action.edit'),
                        onSelect: () => {
                          formRestoreRef.current =
                            plateButtonRefs.current.get(vehicle.uuid) ?? null;
                          setFormTarget({
                            mode: 'edit',
                            vehicle,
                            plateLocked: !canEditPlate(vehicle, 'host'),
                          });
                        },
                      },
                      {
                        key: 'delete',
                        destructive: true,
                        // Devre dışı öğe nedenini etiketinde taşır; "Açık izin" sütunu klavye için tekrarlar.
                        label: t(
                          vehicleDeleteLabelKey(vehicleDeleteBlock(vehicle, 'host'), 'host'),
                        ),
                        disabled: vehicleDeleteBlock(vehicle, 'host') !== null,
                        onSelect: () => {
                          deleteMutation.reset();
                          focus.rememberOpener(plateButtonRefs.current.get(vehicle.uuid) ?? null);
                          setToDelete(vehicle);
                        },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <VehicleSheet
        open={sheetVehicle !== null}
        onOpenChange={(open) => !open && setSheetUuid(null)}
        actor="host"
        context={sheetVehicle ? { kind: 'registry', vehicle: sheetVehicle } : null}
        restoreFocusRef={plateOpenerRef}
        onEscapeKeyDown={escapeStaysInReviewRow}
        documents={
          sheetVehicle ? (
            <VehicleDocuments
              contractorUuid={contractorUuid}
              vehicle={sheetVehicle}
              source={{ dataUpdatedAt: query.dataUpdatedAt, errorUpdatedAt: query.errorUpdatedAt }}
            />
          ) : null
        }
      />

      {formTarget ? (
        <VehicleFormSheet
          key={formTarget.mode === 'edit' ? formTarget.vehicle.uuid : 'create'}
          client={vehicleClient}
          target={formTarget}
          onClose={() => setFormTarget(null)}
          restoreFocusRef={formRestoreRef}
        />
      ) : null}

      <VehicleDocumentLinkModal
        // Her yeni açılışta dosya/tür/not/hata durumu sıfırlansın (hedef kalkıp dönse bile).
        key={uploadSession}
        open={uploadVehicle !== null}
        onOpenChange={(open) => !open && setUploadUuid(null)}
        contractorUuid={contractorUuid}
        vehicleUuid={uploadUuid ?? ''}
        vehiclePlate={uploadVehicle?.plate ?? ''}
        restoreFocusRef={focus.menuRestoreRef}
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title={t('vehicles.delete.title')}
        body={t('vehicles.delete.body', { plate: toDelete?.plate ?? '' })}
        confirmLabel={t('vehicles.delete.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
